'use client';

import { useEffect, useRef } from 'react';

const services = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="3"/>
        <path d="M3 9h18M9 21V9"/>
      </svg>
    ),
    title: 'Website Creation',
    tag: 'Web Development',
    description:
      'From landing pages to full-scale web applications, we design and develop high-performance websites that convert visitors into customers.',
    deliverables: [
      'Custom UI/UX design (Figma)',
      'Responsive front-end development',
      'CMS integration (WordPress, Webflow, Next.js)',
      'SEO-optimized structure & metadata',
      'Performance optimization (Core Web Vitals)',
      'Cross-browser compatibility testing',
      'Post-launch support & documentation',
    ],
    cta: 'Request Website Quote',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="7" y="2" width="10" height="20" rx="2"/>
        <path d="M11 18h2"/>
      </svg>
    ),
    title: 'Mobile Apps',
    tag: 'iOS & Android',
    description:
      'Native and cross-platform mobile applications built with modern frameworks — delivering smooth experiences on both iOS and Android.',
    deliverables: [
      'Product discovery & feature scoping',
      'UX wireframes & interactive prototypes',
      'React Native or Flutter development',
      'API integration & backend connectivity',
      'App Store & Google Play submission',
      'Push notifications & analytics setup',
      'Maintenance & version update support',
    ],
    cta: 'Request App Quote',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="2" width="20" height="20" rx="5"/>
        <circle cx="12" cy="12" r="3"/>
        <path d="M16.5 7.5h.01"/>
      </svg>
    ),
    title: 'Social Media Posts',
    tag: 'Content Design',
    description:
      'Scroll-stopping visual content designed for Instagram, LinkedIn, Facebook, and TikTok — consistent, on-brand, and built to engage.',
    deliverables: [
      'Brand-aligned visual templates',
      'Monthly post calendar & scheduling',
      'Static posts, carousels & reels covers',
      'Story and highlight cover designs',
      'Caption copywriting (English)',
      'Platform-specific sizing & formatting',
      'Delivery in editable source files',
    ],
    cta: 'Request Content Quote',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
      </svg>
    ),
    title: 'Rebranding',
    tag: 'Brand Identity',
    description:
      'Complete brand identity overhauls — from logo redesign to full brand guidelines — helping your business project the right image globally.',
    deliverables: [
      'Brand audit & competitive analysis',
      'Logo redesign (primary + variations)',
      'Color palette & typography system',
      'Brand guidelines document (PDF)',
      'Business card & letterhead design',
      'Social media profile kit',
      'Brand asset delivery (SVG, PNG, PDF)',
    ],
    cta: 'Request Branding Quote',
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#F8FAFF' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="section-label">What We Do</span>
          <div className="section-divider mx-auto" />
          <h2
            className="font-heading font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
          >
            Four Core Services.<br />Infinite Possibilities.
          </h2>
          <p className="text-base leading-relaxed max-w-xl mx-auto" style={{ color: '#6B7280' }}>
            Every service is scoped, priced transparently, and delivered by experienced professionals — no outsourcing, no surprises.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-7">
          {services?.map((service, idx) => (
            <div
              key={service?.title}
              className="service-card reveal-hidden"
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className="flex items-start justify-between mb-5">
                <div className="service-icon-wrap" style={{ color: '#1F3A8A' }}>
                  {service?.icon}
                </div>
                <span
                  className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ background: '#EEF2FF', color: '#1F3A8A' }}
                >
                  {service?.tag}
                </span>
              </div>

              <h3
                className="font-heading font-bold mb-3"
                style={{ fontSize: '20px', color: '#0D1A3A' }}
              >
                {service?.title}
              </h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                {service?.description}
              </p>

              <div className="mb-6">
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: '#9CA3AF' }}
                >
                  What&apos;s Included
                </p>
                <ul className="check-list">
                  {service?.deliverables?.map((d) => (
                    <li key={d}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1F3A8A"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        className="flex-shrink-0 mt-0.5"
                      >
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="#contact"
                className="btn-primary w-full justify-center"
                style={{ fontSize: '14px', padding: '12px 20px' }}
              >
                {service?.cta}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}